<template>
  <div class="ticket-info-page">
    <div class="ticket-top">
      <div class="d-flex align-start justify-space-between" style="gap: 12px">
        <div class="flex-grow-1">
          <div class="text-h6 font-weight-bold mb-2">{{ ticket.subject }}</div>
          <div class="d-flex align-center flex-wrap header-meta" style="gap: 10px">
            <span class="text-body-2 text-medium-emphasis">
              Case ID: {{ ticket.caseId }}
            </span>
            <span class="meta-dot">•</span>
            <span class="text-body-2 text-medium-emphasis">
              {{ chatTurns.length }} {{ chatTurns.length === 1 ? "log" : "logs" }}
            </span>
          </div>
        </div>

        <v-btn
          variant="text"
          color="primary"
          prepend-icon="mdi-arrow-left"
          class="text-none flex-shrink-0"
          @click="$emit('back')"
        >
          Back
        </v-btn>
      </div>

      <v-divider class="mt-4" />
    </div>

    <div class="ticket-middle">
      <div class="correspondence-header">
        <div class="text-subtitle-1 font-weight-bold">Chat logs</div>
        <div class="text-body-2 text-medium-emphasis">
          AI conversation that created this case
        </div>
      </div>

      <div class="messages-scroll">
        <div v-if="messages.length" class="timeline">
          <div
            v-for="(msg, index) in messages"
            :key="msg.id || `${msg.time}-${index}`"
            class="timeline-item"
            :class="{ 'is-last': index === messages.length - 1 }"
          >
            <div class="timeline-rail">
              <span
                class="timeline-node"
                :class="{ 'node-bot': msg.type === 'bot' }"
              ></span>
              <div v-if="index !== messages.length - 1" class="timeline-line"></div>
            </div>

            <div class="timeline-content">
              <div class="d-flex align-start" style="gap: 12px">
                <v-avatar :color="msg.type === 'bot' ? 'warning' : 'grey'" size="36" class="flex-shrink-0">
                  <span class="text-white text-caption font-weight-bold">
                    {{ msg.type === "bot" ? "AI" : avatarLabel(msg.sender) }}
                  </span>
                </v-avatar>

                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                    <span class="font-weight-bold">{{ msg.sender }}</span>
                    <v-chip
                      v-if="msg.type === 'bot'"
                      size="x-small"
                      color="warning"
                      variant="tonal"
                    >
                      AI
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">• {{ msg.time }}</span>
                  </div>

                  <div class="message-bubble" :class="`bubble-${msg.type}`">
                    {{ msg.text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-correspondence">
          <v-icon size="40" color="grey-lighten-1">mdi-robot-outline</v-icon>
          <div class="text-body-2 text-medium-emphasis mt-2">No chat logs for this case.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TicketChatLogs",
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  emits: ["back"],
  computed: {
    chatTurns() {
      return Array.isArray(this.ticket?.chatbotLogs) ? this.ticket.chatbotLogs : [];
    },
    reporterName() {
      return this.ticket?.reporter || this.ticket?.reporterEmail || "User";
    },
    messages() {
      const rows = [];
      this.chatTurns.forEach((log, index) => {
        if (log.query) {
          rows.push({
            id: `${log.id || index}-query`,
            type: "user",
            sender: this.reporterName,
            time: log.time,
            text: log.query,
          });
        }
        if (log.response) {
          rows.push({
            id: `${log.id || index}-response`,
            type: "bot",
            sender: "RCS Support Bot",
            time: log.time,
            text: log.response,
          });
        }
      });
      return rows;
    },
  },
  methods: {
    avatarLabel(name) {
      return String(name || "?").charAt(0).toUpperCase();
    },
  },
};
</script>

<style scoped>
.ticket-info-page {
  height: calc(100dvh - 64px);
  max-height: calc(100dvh - 64px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 8px 16px 12px;
  box-sizing: border-box;
  background: #fff;
}

.ticket-top {
  flex-shrink: 0;
}

.ticket-middle {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  overflow: hidden;
}

.correspondence-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.messages-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 12px;
}

.meta-dot {
  color: rgba(0, 0, 0, 0.3);
}

.timeline {
  display: flex;
  flex-direction: column;
}

.timeline-item {
  display: flex;
  gap: 10px;
}

.timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 14px;
  flex-shrink: 0;
  padding-top: 12px;
}

.timeline-node {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #b0b8c4;
  background: #fff;
  z-index: 1;
}

.timeline-node.node-bot {
  border-color: #fb8c00;
  background: #fb8c00;
}

.timeline-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: #d7dde7;
  margin-top: 4px;
}

.timeline-content {
  flex: 1;
  padding-bottom: 16px;
  min-width: 0;
}

.timeline-item.is-last .timeline-content {
  padding-bottom: 8px;
}

.message-bubble {
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.78);
  background: #fff;
  white-space: pre-wrap;
}

.bubble-bot {
  background: #fff8f0;
  border-color: #ffe0b2;
}

.empty-correspondence {
  text-align: center;
  padding: 40px 16px;
}

.min-w-0 {
  min-width: 0;
}
</style>
